import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { CheckCircle2, XCircle, FileText, Building2, Calendar, User, Shield, ArrowLeft } from 'lucide-react';
import api from '../lib/api';
import { BrandLoader } from '../components/BrandLoader';

const ROLE_LABELS = {
  'EXEC_DIRECTOR': 'Executive Director',
  'DIRECTOR': 'Director',
  'COMPANY_REP': 'Company Representative',
  'PI': 'Principal Investigator',
  'CO_PI': 'Co-Principal Investigator',
  'EXTERNAL': 'External Signatory'
};

export default function VerifyPage() {
  const { verificationId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verificationData, setVerificationData] = useState(null);

  // Canonical production host. If a user scans an OLD QR that points to a non-production host
  // (e.g. an early preview URL) and the verification ID isn't found here, we forward them to the
  // canonical production verify URL. This keeps already-printed QR codes working.
  const CANONICAL_VERIFY_HOST = 'clinicalflow.aretion.co.uk';

  useEffect(() => {
    const verifyDocument = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/verify/${verificationId}`);
        setVerificationData(response.data);
      } catch (err) {
        // If the verification ID isn't on this environment AND we aren't already on the canonical
        // production host, transparently forward the browser to production where the record lives.
        // Always forward away from internal platform hostnames (.emergent.host, .preview.emergentagent.com)
        // even if the API responded successfully — those URLs should never appear in users' browsers.
        const status = err.response?.status;
        const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
        const isInternalHost = /\.emergent\.host$|\.preview\.emergentagent\.com$|\.emergentagent\.com$/.test(currentHost);
        if (status === 404 && currentHost && currentHost !== CANONICAL_VERIFY_HOST) {
          window.location.replace(`https://${CANONICAL_VERIFY_HOST}/verify/${verificationId}`);
          return; // browser is navigating away; don't bother updating state
        }
        if (isInternalHost) {
          // Even on a non-404 outcome, never let users land on an internal platform hostname.
          window.location.replace(`https://${CANONICAL_VERIFY_HOST}/verify/${verificationId}`);
          return;
        }
        console.error('Verification failed:', err);
        setError(err.response?.data?.detail || 'Verification failed. This document may not be valid.');
      } finally {
        setLoading(false);
      }
    };

    if (verificationId) {
      verifyDocument();
    }
  }, [verificationId]);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <BrandLoader size="lg" label="Verifying document…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Verification Failed</h2>
              <p className="text-slate-600 mb-6">{error}</p>
              <Link to="/login">
                <Button variant="outline">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Go to ClinicalFlow
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-emerald-100 rounded-full mb-4">
            <Shield className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Document Verified</h1>
          <p className="text-slate-600 mt-2">This document has been authenticated</p>
        </div>

        {/* Verification Badge */}
        <Card className="mb-6 border-emerald-200 bg-emerald-50/50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-emerald-900">Authentic Document</h3>
                <p className="text-sm text-emerald-700 mt-1">
                  {verificationData?.message}
                </p>
                <div className="mt-3 p-2 bg-white rounded border border-emerald-200">
                  <p className="text-xs text-slate-500">Verification ID</p>
                  <p className="font-mono text-sm text-slate-900">{verificationId}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Document Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5" />
              Document Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* iter167 Issue 15 — Stack on mobile, 2-col from sm: up.
                `break-all` on filename so long unbroken strings (e.g.
                Signed_AVEIR_Registry_SA4221_KFMC_CTA_21Apr26-signed by A_20260620_212037.pdf)
                wrap within their column instead of overlapping the
                neighbouring "Document Type" column. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="min-w-0">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Document Name</p>
                <p className="font-medium text-slate-900 break-all">{verificationData?.document?.filename}</p>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Document Type</p>
                <Badge variant="outline" className="break-all whitespace-normal">{verificationData?.document?.doc_type}</Badge>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Signed Date</p>
                <p className="text-slate-900">{formatDateTime(verificationData?.document?.signed_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Request Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="w-5 h-5" />
              Request Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="min-w-0">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Reference</p>
                <p className="font-mono text-slate-900 break-all">{verificationData?.request?.ref_no}</p>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Type</p>
                <Badge>{verificationData?.request?.type}</Badge>
              </div>
              <div className="sm:col-span-2 min-w-0">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Title</p>
                <p className="text-slate-900 break-words">{verificationData?.request?.title}</p>
              </div>
              {verificationData?.request?.company && (
                <div className="sm:col-span-2 min-w-0">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Company</p>
                  <p className="text-slate-900 break-words">{verificationData?.request?.company}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Signatures */}
        {verificationData?.signatures?.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="w-5 h-5" />
                Electronic Signatures
              </CardTitle>
              <CardDescription>
                {verificationData.signatures.length} signature(s) collected
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {verificationData.signatures.map((sig, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{sig.name}</p>
                        <p className="text-xs text-slate-500">
                          {ROLE_LABELS[sig.role] || sig.role}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-serif italic text-emerald-700">{sig.typed_name}</p>
                      <p className="text-xs text-slate-500">
                        {formatDateTime(sig.signed_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-sm text-slate-500">
          <p>Verified by ClinicalFlow E-Signature System</p>
          <Link to="/login" className="text-blue-600 hover:underline mt-2 inline-block">
            Go to ClinicalFlow
          </Link>
        </div>
      </div>
    </div>
  );
}
